<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { syncEngine } from '../../services/sync/syncEngine'
import { askConfirm } from '../../services/ui/confirmDialog'
import '../settings/settings.css'
import AccountExportStep from './AccountExportStep.vue'
import SettingSection from '../settings/controls/SettingSection.vue'
import SettingRow from '../settings/controls/SettingRow.vue'

defineProps<{ editor?: any }>()
const auth = useAuthStore()
const showRemoval = ref(false)
const ready = ref(false)
const busy = ref(false)
const actionError = ref<string | null>(null)

async function lock() {
  busy.value = true
  actionError.value = null
  try { await auth.lockKeySession() }
  catch (cause) { actionError.value = cause instanceof Error ? cause.message : String(cause) }
  finally { busy.value = false }
}
async function remove() {
  const pending = await syncEngine.refreshPendingCount().catch(() => syncEngine.pendingCount.value)
  if (!await askConfirm({
    title: 'Remove all local data from this device?',
    message: 'This removes writing, saved versions, encryption keys, the local license and settings. You will need an online sign-in to use this device again or change accounts. Backups and folders you chose yourself remain.'
      + (pending ? `\n\n${pending} pending changes will be permanently discarded.` : ''),
    confirmLabel: 'Remove local data', danger: true,
  })) return
  busy.value = true
  actionError.value = null
  try { await auth.removeLocalData() }
  catch (cause) { actionError.value = auth.error || String(cause) }
  finally { busy.value = false }
}
</script>
<template>
  <SettingSection title="This device">
    <SettingRow v-if="auth.isKeySessionUnlocked" label="Lock writing" description="Close local writing and sign out of cloud sync. Unlock offline with the last password signed in on this device. Password changes elsewhere take effect here after your next successful sign-in.">
      <button class="s-btn" type="button" :disabled="busy || auth.loading" @click="lock">Lock writing</button>
    </SettingRow>
    <SettingRow label="Remove local data / change account" description="Export first, then explicitly remove this device’s writing and credentials to start fresh or use another account.">
      <button class="s-btn danger" type="button" :disabled="busy" @click="showRemoval = !showRemoval">{{ showRemoval ? 'Cancel removal' : 'Remove local data…' }}</button>
      <template v-if="showRemoval" #below>
        <AccountExportStep v-if="auth.isKeySessionUnlocked" v-model:ready="ready" :editor="editor" :disabled="busy" />
        <label v-else class="s-check"><input v-model="ready" type="checkbox" :disabled="busy" /> I understand that locked writing cannot be exported here and will be permanently removed.</label>
        <p class="s-note">Removal also discards changes that have not reached the cloud.</p>
        <button class="s-btn danger" type="button" :disabled="!ready || busy" @click="remove">{{ busy ? 'Removing…' : 'Remove local data' }}</button>
      </template>
    </SettingRow>
    <p v-if="actionError" class="s-note error" role="alert">{{ actionError }}</p>
  </SettingSection>
</template>
